import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Target, Sparkles, RefreshCw, ChevronLeft, ChevronRight, Save, ExternalLink, BellRing } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS = {
  atteint: ["Atteint", "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"],
  en_avance: ["Dans le rythme", "bg-sky-500/15 text-sky-300 border-sky-500/40"],
  en_retard: ["En retard", "bg-amber-500/15 text-amber-300 border-amber-500/40"],
  critique: ["Critique", "bg-rose-500/15 text-rose-300 border-rose-500/40"],
};
const fmt = (v, unit) => (unit === "$" ? `${Number(v || 0).toLocaleString("fr-CA", { maximumFractionDigits: 0 })} $` : `${Number(v || 0).toLocaleString("fr-CA", { maximumFractionDigits: 1 })}${unit}`);
const monthLabel = (m) => new Date(`${m}-15T12:00:00`).toLocaleDateString("fr-CA", { month: "long", year: "numeric" });
const shift = (m, d) => { const [y, mo] = m.split("-").map(Number); const dt = new Date(y, mo - 1 + d, 1); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`; };

function KpiCard({ k, target, value, pace, onTarget }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(target);
  useEffect(() => setVal(target), [target]);
  const st = STATUS[pace.status] || STATUS.en_retard;
  return (
    <div className="border border-white/10 bg-black/30 p-4 flex flex-col gap-2" data-testid={`kpi-${k.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div><p className="text-[11px] uppercase tracking-wider text-white/50">{k.label}</p><p className="text-[10px] text-white/30 mt-0.5">{k.help}</p></div>
        <span className={`text-[10px] border px-2 py-0.5 whitespace-nowrap ${st[1]}`} data-testid={`kpi-status-${k.key}`}>{st[0]}</span>
      </div>
      <div className="flex items-end justify-between gap-2">
        <div><span className="text-2xl font-bold text-white" data-testid={`kpi-value-${k.key}`}>{fmt(value, k.unit)}</span>
          <span className="text-xs text-white/40"> / {edit ? (
            <input type="number" value={val} onChange={(e) => setVal(e.target.value)} onBlur={() => { setEdit(false); if (Number(val) !== Number(target)) onTarget(Number(val)); }} autoFocus
              className="w-24 bg-zinc-900 border border-amber-500/50 text-white text-xs px-1 py-0.5" data-testid={`kpi-target-input-${k.key}`} />
          ) : <button onClick={() => setEdit(true)} className="underline decoration-dotted hover:text-amber-300" data-testid={`kpi-target-${k.key}`}>{fmt(target, k.unit)}</button>}</span></div>
        <span className="text-xs text-white/50">{pace.pct}%</span>
      </div>
      <div className="relative h-2 bg-white/10">
        <div className={`h-full ${pace.status === "atteint" ? "bg-emerald-400" : pace.status === "en_avance" ? "bg-sky-400" : pace.status === "en_retard" ? "bg-amber-400" : "bg-rose-500"}`} style={{ width: `${Math.min(100, pace.pct)}%` }} />
        {target > 0 && <div className="absolute top-[-3px] h-[14px] w-[2px] bg-white/60" style={{ left: `${Math.min(100, (pace.expected_now / target) * 100)}%` }} title={`Attendu aujourd'hui : ${fmt(pace.expected_now, k.unit)}`} />}
      </div>
      <p className="text-[11px] text-white/50">Attendu au jour {pace.day}/{pace.days} : <b className="text-white/70">{fmt(pace.expected_now, k.unit)}</b>{pace.status !== "atteint" && pace.remaining_days > 0 && <> · reste <b className="text-white/70">{fmt(pace.gap, k.unit)}</b> → <b className="text-amber-300">{fmt(pace.needed_per_day, k.unit)}/jour</b></>}</p>
    </div>
  );
}

function Diagnostic({ items }) {
  if (!items?.length) return <div className="border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-emerald-300" data-testid="diag-empty">Tous les objectifs sont dans le rythme ✅</div>;
  return (
    <div className="space-y-3" data-testid="diagnostic">
      {items.map((it) => (
        <div key={it.kpi} className="border border-white/10 bg-black/30 p-4" data-testid={`diag-${it.kpi}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-white">{it.label} — <span className={STATUS[it.status]?.[1].split(" ")[1]}>{STATUS[it.status]?.[0]}</span></h4>
            <span className="text-xs text-white/40">{it.actual} / {it.target} · attendu {it.pace.expected_now}</span>
          </div>
          <ul className="mt-2 space-y-2">
            {it.causes.map((c, i) => (
              <li key={i} className="grid md:grid-cols-[1fr_1fr] gap-2 text-xs border-l-2 border-amber-500/50 pl-3">
                <div><p className="text-rose-300 font-medium">✗ {c.erreur}</p><p className="text-white/40 mt-0.5">{c.impact}</p></div>
                <div className="flex items-start justify-between gap-2"><p className="text-emerald-300">✓ {c.correction}</p>{c.lien && <Link to={c.lien} className="text-white/40 hover:text-amber-300 shrink-0" title="Ouvrir"><ExternalLink size={13} /></Link>}</div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function AiReview({ review, onRun, busy }) {
  const ai = review?.ai;
  return (
    <div className="border border-[#C9A961]/30 bg-[#C9A961]/5 p-4" data-testid="ai-review">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Sparkles size={15} className="text-[#C9A961]" /> Programme de correction (IA)</h3>
        <button onClick={onRun} disabled={busy} data-testid="ai-review-run" className="bg-[#C9A961] hover:bg-[#b8964f] disabled:opacity-50 text-black text-xs font-semibold px-3 py-1.5 flex items-center gap-1.5">
          <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> {busy ? "Analyse…" : ai ? "Régénérer" : "Analyser pourquoi"}
        </button>
      </div>
      {!ai && <p className="text-xs text-white/40 mt-2">L'IA lit le diagnostic chiffré et rédige : erreurs commises, ce que ça coûte, comment corriger, plan 7 jours et rituels pour toujours atteindre l'objectif.</p>}
      {ai && (
        <div className="mt-3 space-y-4 text-sm">
          <p className="text-white/80" data-testid="ai-resume">{ai.resume}</p>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-white/40 mb-2">Erreurs → corrections</p>
            <ol className="space-y-2">{(ai.erreurs || []).map((e, i) => (
              <li key={i} className="border border-white/10 bg-black/30 p-3 grid md:grid-cols-[auto_1fr_1fr] gap-3" data-testid={`ai-erreur-${i}`}>
                <span className="text-lg font-bold text-[#C9A961]">#{e.priorite ?? i + 1}</span>
                <div><p className="text-rose-300">{e.erreur}</p><p className="text-xs text-white/40 mt-1">{e.pourquoi_ca_coute}</p></div>
                <p className="text-emerald-300">{e.correction}</p>
              </li>))}</ol>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><p className="text-[11px] uppercase tracking-wider text-white/40 mb-2">Plan 7 jours</p>
              <ul className="space-y-1 text-xs">{(ai.plan_7_jours || []).map((p, i) => <li key={i} className="flex gap-2"><b className="text-white/70 w-20 shrink-0">{p.jour}</b><span className="text-white/80">{p.action} <span className="text-white/40">→ {p.resultat_attendu}</span></span></li>)}</ul></div>
            <div><p className="text-[11px] uppercase tracking-wider text-white/40 mb-2">Rituels pour toujours atteindre l'objectif</p>
              <ul className="space-y-1 text-xs">{(ai.rituel_pour_toujours_atteindre || []).map((r, i) => <li key={i} className="text-white/80">• {r}</li>)}</ul></div>
          </div>
          {ai.message_motivation && <p className="text-[#C9A961] italic">« {ai.message_motivation} »</p>}
          <p className="text-[10px] text-white/30">Généré le {review.at?.slice(0, 16).replace("T", " ")} · score {review.score}/100</p>
        </div>
      )}
    </div>
  );
}


function FridayAlert() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const load = useCallback(() => api.get("/objectifs/alerte/preview", { timeout: 90000 }).then((r) => setD(r.data)).catch(() => setD({ error: true })), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async () => { await api.put("/objectifs/alerte/config", { enabled: !d.config.enabled, recipient: d.config.recipient }); toast.success(!d.config.enabled ? "Alerte du vendredi ACTIVÉE" : "Alerte du vendredi désactivée"); load(); };
  const send = async () => {
    if (!window.confirm(`Envoyer MAINTENANT le point du vendredi (score ${d.score}/100) à ${d.config.recipient} ?`)) return;
    setBusy(true);
    try { const r = await api.post("/objectifs/alerte/send-now?confirm=true", {}, { timeout: 90000 }); toast.success(r.data.sent ? `Envoyé à ${r.data.to}` : "Non envoyé"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec"); } finally { setBusy(false); }
  };
  if (!d || d.error) return null;
  return (
    <div className="mt-6 border border-white/10 bg-black/30 p-4" data-testid="friday-alert">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div><h3 className="text-sm font-semibold text-white flex items-center gap-2"><BellRing size={15} className="text-[#C9A961]" /> Rappel du vendredi — score du mois + 3 corrections prioritaires</h3>
          <p className="text-[11px] text-white/40 mt-0.5">Courriel automatique chaque vendredi 8 h à {d.config.recipient}{d.config.last_run?.at ? ` · dernier envoi ${d.config.last_run.at.slice(0, 10)}` : ""}</p></div>
        <div className="flex gap-2 items-center">
          <button onClick={toggle} data-testid="friday-toggle" className={`text-xs px-3 py-1.5 border ${d.config.enabled ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" : "border-amber-500/40 text-amber-400 bg-amber-500/10"}`}>Vendredi 8 h : {d.config.enabled ? "ACTIVÉ" : "DÉSACTIVÉ"}</button>
          <button onClick={send} disabled={busy} data-testid="friday-send" className="border border-white/20 hover:bg-white/10 disabled:opacity-50 text-white/80 text-xs px-3 py-1.5">Envoyer maintenant</button>
          <button onClick={() => setShow((v) => !v)} data-testid="friday-preview-toggle" className="text-xs text-white/60 underline">{show ? "Masquer" : "Aperçu"}</button>
        </div>
      </div>
      <ol className="mt-3 text-xs space-y-1" data-testid="friday-top3">{d.top3.map((t, i) => <li key={i}><span className="text-rose-300">✗ {t.erreur}</span> <span className="text-emerald-300">→ {t.correction}</span></li>)}</ol>
      {show && <div className="mt-3 bg-white p-3 text-black text-xs" dangerouslySetInnerHTML={{ __html: d.html }} />}
    </div>
  );
}

export default function ObjectifsMoisModule() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [hist, setHist] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/objectifs/mois", { params: { month }, timeout: 90000 }); setData(data); setNotes(data.notes || ""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Chargement impossible"); }
  }, [month]);
  useEffect(() => { setData(null); load(); }, [load]);
  useEffect(() => { api.get("/objectifs/historique", { timeout: 120000 }).then((r) => setHist(r.data.rows)).catch(() => setHist([])); }, []);

  const saveTargets = async (targets, n = notes) => {
    try { await api.put(`/objectifs/mois/${month}/cibles`, { targets, notes: n }); toast.success("Cibles enregistrées"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };
  const runAi = async () => {
    setBusy(true);
    try { await api.post(`/objectifs/mois/${month}/revue`, {}, { timeout: 120000 }); toast.success("Programme de correction prêt"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "IA indisponible"); } finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="objectifs-mois-module">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#C9A961]">Comptabiliser · pilotage</p>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2"><Target size={24} className="text-[#D90429]" /> Objectifs du mois</h1>
          <p className="text-sm text-white/50 mt-1">Cibles, réalisé en direct, rythme attendu, et le programme qui explique pourquoi un objectif est raté et comment le corriger.</p>
        </div>
        <div className="flex items-center gap-2" data-testid="month-nav">
          <button onClick={() => setMonth(shift(month, -1))} className="border border-white/15 p-2 hover:bg-white/10" data-testid="month-prev"><ChevronLeft size={16} /></button>
          <span className="text-white font-semibold capitalize min-w-[150px] text-center" data-testid="month-label">{monthLabel(month)}</span>
          <button onClick={() => setMonth(shift(month, 1))} className="border border-white/15 p-2 hover:bg-white/10" data-testid="month-next"><ChevronRight size={16} /></button>
        </div>
      </div>

      {!data ? <p className="mt-8 text-white/50 text-sm" data-testid="objectifs-loading">Calcul en cours (Stripe + base)…</p> : (
        <>
          <div className="mt-6 flex items-center gap-4 flex-wrap">
            <div className="border border-white/10 bg-black/30 px-5 py-3"><p className="text-[11px] uppercase tracking-wider text-white/50">Score du mois</p><p className="text-3xl font-bold text-white" data-testid="score">{data.score}<span className="text-sm text-white/40">/100</span></p></div>
            {data.from_previous && <p className="text-xs text-amber-300" data-testid="targets-from-previous">Cibles reprises du mois précédent — clique une cible pour l'ajuster.</p>}
          </div>
          <div className="mt-4 grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {data.kpis.map((k) => <KpiCard key={k.key} k={k} target={Number(data.targets[k.key] || 0)} value={data.actuals.values[k.key]} pace={data.paces[k.key]} onTarget={(v) => saveTargets({ ...data.targets, [k.key]: v })} />)}
            <div className="border border-white/10 bg-black/30 p-4 flex flex-col gap-2">
              <p className="text-[11px] uppercase tracking-wider text-white/50">Notes du mois</p>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="bg-zinc-900 border border-white/10 text-white text-xs p-2 flex-1" placeholder="Contexte, promos, événements…" data-testid="notes-input" />
              <button onClick={() => saveTargets(data.targets, notes)} className="self-end text-xs text-white/70 hover:text-white flex items-center gap-1" data-testid="notes-save"><Save size={12} /> Enregistrer</button>
            </div>
          </div>

          <h2 className="mt-8 text-base md:text-lg font-semibold text-white">Pourquoi l'objectif n'est pas atteint — diagnostic automatique</h2>
          <p className="text-xs text-white/40 mb-3">Règles appliquées aux données réelles : chaque erreur détectée vient avec sa correction et le lien vers la page concernée.</p>
          <Diagnostic items={data.diagnostic} />

          <div className="mt-6"><AiReview review={data.review} onRun={runAi} busy={busy} /></div>
          <FridayAlert />

          <h2 className="mt-8 text-base md:text-lg font-semibold text-white">Historique 6 mois</h2>
          {!hist ? <p className="text-xs text-white/40">Calcul…</p> : (
            <div className="overflow-x-auto mt-2" data-testid="history">
              <table className="w-full text-xs"><thead><tr className="text-white/40 text-left"><th className="py-1 pr-3">Mois</th>{data.kpis.map((k) => <th key={k.key} className="py-1 pr-3">{k.label}</th>)}<th>Score</th></tr></thead>
                <tbody>{hist.map((r) => <tr key={r.month} className="border-t border-white/5 text-white/80"><td className="py-1.5 pr-3 capitalize">{monthLabel(r.month)}</td>{data.kpis.map((k) => <td key={k.key} className="py-1.5 pr-3">{fmt(r.values[k.key], k.unit)} <span className="text-white/30">/ {fmt(r.targets[k.key], k.unit)}</span></td>)}<td className={`font-semibold ${r.score >= 80 ? "text-emerald-300" : r.score >= 50 ? "text-amber-300" : "text-rose-300"}`}>{r.score}</td></tr>)}</tbody></table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
